import { useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../../../lib/api";
import { Modal, Input, Textarea } from "./common";

export function VitalsModal({ patientId, existing = null, onClose, onSaved }) {
  const editing = Boolean(existing?.id);
  const ev = existing?.vitals || {};
  const [form, setForm] = useState({
    diagnosis: existing?.diagnosis || "Registro de signos vitales",
    blood_pressure: ev.blood_pressure || "",
    heart_rate: ev.heart_rate || "",
    temperature: ev.temperature || "",
    respiration_rate: ev.respiration_rate || "",
    oxygen_saturation: ev.oxygen_saturation || "",
    weight: ev.weight || "",
    height: ev.height || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { diagnosis, ...vitals } = form;
      if (editing) {
        await api.put(`/diagnoses/${existing.id}`, {
          patient_id: patientId,
          diagnosis: diagnosis || "Registro de signos vitales",
          symptoms: existing.symptoms || null,
          treatment: existing.treatment || null,
          notes: existing.notes || null,
          physical_exam: existing.physical_exam || null,
          appointment_id: existing.appointment_id || null,
          vitals,
        });
        toast.success("Vitales actualizados");
      } else {
        await api.post("/diagnoses", {
          patient_id: patientId,
          diagnosis: diagnosis || "Registro de signos vitales",
          vitals,
        });
        toast.success("Vitales registrados");
      }
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("¿Eliminar este registro de vitales?")) return;
    setSaving(true);
    try {
      await api.delete(`/diagnoses/${existing.id}`);
      toast.success("Registro eliminado");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Editar signos vitales" : "Registrar signos vitales"} onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <Input
          label="Etiqueta"
          value={form.diagnosis}
          onChange={(v) => set("diagnosis", v)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Presión arterial"
            placeholder="120/80"
            value={form.blood_pressure}
            onChange={(v) => set("blood_pressure", v)}
          />
          <Input
            label="Frecuencia cardiaca (bpm)"
            placeholder="72"
            value={form.heart_rate}
            onChange={(v) => set("heart_rate", v)}
          />
          <Input
            label="Temperatura (°C)"
            placeholder="36.5"
            value={form.temperature}
            onChange={(v) => set("temperature", v)}
          />
          <Input
            label="Frec. respiratoria"
            placeholder="16"
            value={form.respiration_rate}
            onChange={(v) => set("respiration_rate", v)}
          />
          <Input
            label="SpO₂ (%)"
            placeholder="98"
            value={form.oxygen_saturation}
            onChange={(v) => set("oxygen_saturation", v)}
          />
          <Input
            label="Peso (kg)"
            placeholder="70"
            value={form.weight}
            onChange={(v) => set("weight", v)}
          />
          <Input
            label="Altura (cm)"
            placeholder="170"
            value={form.height}
            onChange={(v) => set("height", v)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          {editing && (
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              data-testid="vitals-delete"
              className="mr-auto h-10 px-4 rounded-lg bg-red-50 text-red-600 font-semibold text-[13px] hover:bg-red-100 transition"
            >
              Eliminar
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-lg bg-white border border-black/10 text-black font-semibold text-[13px] hover:bg-black/[0.04] transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="vitals-save"
            className="h-10 px-5 rounded-lg bg-black hover:bg-black/85 text-white font-semibold text-[13px] transition disabled:opacity-60"
          >
            {saving ? "Guardando…" : editing ? "Guardar cambios" : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function NoteModal({ patientId, existing = null, onClose, onSaved }) {
  const editing = Boolean(existing?.id);
  const [form, setForm] = useState({
    diagnosis: existing?.diagnosis || "",
    symptoms: existing?.symptoms || "",
    notes: existing?.notes || "",
    treatment: existing?.treatment || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.diagnosis.trim()) {
      toast.error("Indica un título o diagnóstico");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/diagnoses/${existing.id}`, {
          ...form,
          patient_id: patientId,
          appointment_id: existing.appointment_id || null,
          vitals: existing.vitals || null,
          physical_exam: existing.physical_exam || null,
        });
        toast.success("Nota actualizada");
      } else {
        await api.post("/diagnoses", { ...form, patient_id: patientId });
        toast.success("Nota guardada");
      }
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("¿Eliminar esta nota?")) return;
    setSaving(true);
    try {
      await api.delete(`/diagnoses/${existing.id}`);
      toast.success("Nota eliminada");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Editar nota clínica" : "Nueva nota clínica"} onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <Input
          label="Diagnóstico / Título"
          value={form.diagnosis}
          onChange={(v) => set("diagnosis", v)}
          placeholder="Ej. Migraña, control rutinario..."
          required
        />
        <Textarea
          label="Síntomas"
          value={form.symptoms}
          onChange={(v) => set("symptoms", v)}
          rows={2}
        />
        <Textarea
          label="Tratamiento"
          value={form.treatment}
          onChange={(v) => set("treatment", v)}
          rows={2}
        />
        <Textarea
          label="Notas"
          value={form.notes}
          onChange={(v) => set("notes", v)}
          rows={3}
        />
        <div className="flex justify-end gap-2 pt-2">
          {editing && (
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              data-testid="note-delete"
              className="mr-auto h-10 px-4 rounded-lg bg-red-50 text-red-600 font-semibold text-[13px] hover:bg-red-100 transition"
            >
              Eliminar
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-lg bg-white border border-black/10 text-black font-semibold text-[13px] hover:bg-black/[0.04] transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="note-save"
            className="h-10 px-5 rounded-lg bg-black hover:bg-black/85 text-white font-semibold text-[13px] transition disabled:opacity-60"
          >
            {saving ? "Guardando…" : editing ? "Guardar cambios" : "Guardar nota"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
